import { Button, Checkbox, Col, Form, FormInstance, Input, message, Row, Select, Space } from 'antd';
import styles from './UserPage.module.scss';
import Icon from '@ant-design/icons';
import { ReactComponent as SaveSvg } from '../../../../resources/images/save.svg';
import React, { Component } from 'react';
import { FormattedDate, FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link, RouteComponentProps } from 'react-router-dom';
import userApi from '../../../../api/UserApi';
import LayoutComponent from '../../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../../context/CustomContext';
import { User } from '../../../../model/entities';
import { statuses } from '../../../../model/types';
import notificationService from '../../../../services/NotificationService';
import WrapperComponent from '../../../../components/WrapperComponent/WrapperComponent';

class UserPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            if (this.props.match.params.id === 'new') {
                await this.new();
            } else {
                await this.get(+this.props.match.params.id);
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    new = async () => {
        const user: User = {
            status: 'ENABLED',
            sync: false,
            setup: false,
            admin: false,
            issues: false,
            reports: false,
            projectSubmissionNotification: false,
        };

        this.setState({ user });
        this.formRef.current!.setFieldsValue(user);
    };

    get = async (id: number) => {
        const user = await userApi.get(id);

        this.setState({ user });
        this.formRef.current!.setFieldsValue(user);
    };

    save = async (values: any) => {
        try {
            this.setState({ saving: true });
            let user: User = Object.assign({}, this.state.user, values);
            user = user.id ? await userApi.update(user) : await userApi.create(user);
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));

            this.setState({ user });
        } catch (error) {
            notificationService.displayError(error, this.props.intl, [
                { status: 409, message: 'user.status.duplicate' },
            ]);
        } finally {
            this.setState({ saving: false });
        }
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return this.renderForm();
    };

    renderAudit = (user?: User): React.ReactElement | undefined => {
        if (user && user.audit) {
            return (
                <div className={styles.audit}>
                    <p>
                        <FormattedMessage id="user.audit.createdOn" />{' '}
                        <FormattedDate value={user.audit.created as any} dateStyle="medium" timeStyle="short" />{' '}
                        {user.audit.creator && <FormattedMessage id="user.audit.by" />}{' '}
                        {user.audit.creator && `${user.audit.creator.firstName} ${user.audit.creator.lastName}`}
                    </p>
                    <p>
                        <FormattedMessage id="user.audit.updatedOn" />{' '}
                        <FormattedDate value={user.audit.updated as any} dateStyle="medium" timeStyle="short" />{' '}
                        {user.audit.updater && <FormattedMessage id="user.audit.by" />}{' '}
                        {user.audit.updater && `${user.audit.updater.firstName} ${user.audit.updater.lastName}`}
                    </p>
                </div>
            );
        }
    };

    renderForm = (): React.ReactElement | undefined => {
        const { saving, user } = this.state;
        const statusOptions = statuses.map((status) => (
            <Select.Option key={status} value={status}>
                <FormattedMessage id={status} />
            </Select.Option>
        ));

        return (
            <Form ref={this.formRef} onFinish={this.save} colon={false} layout="vertical">
                <Row gutter={[28, 0]}>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage id="user.email" />}
                            name="email"
                            rules={[
                                { required: true, message: <FormattedMessage id="status.mandatory" /> },
                                {
                                    type: 'email',
                                    message: <FormattedMessage id="status.email.invalid" />,
                                },
                            ]}
                        >
                            <Input maxLength={200} size="large" disabled={user?.id === this.context.auth?.id} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage id="user.status" />}
                            name="status"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select size="large">{statusOptions}</Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage id="user.firstName" />}
                            name="firstName"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input maxLength={100} size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage id="user.lastName" />}
                            name="lastName"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input maxLength={100} size="large" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={24} className={styles.label}>
                        {<FormattedMessage id="user.apps" />}
                    </Col>
                    <Col span={4}>
                        <Form.Item
                            name="sync"
                            valuePropName="checked"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Checkbox>
                                <FormattedMessage id="user.sync" />
                            </Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item
                            name="setup"
                            valuePropName="checked"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Checkbox>
                                <FormattedMessage id="user.setup" />
                            </Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item
                            name="reports"
                            valuePropName="checked"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Checkbox>
                                <FormattedMessage id="user.reports" />
                            </Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item
                            name="admin"
                            valuePropName="checked"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Checkbox>
                                <FormattedMessage id="user.admin" />
                            </Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item
                            name="issues"
                            valuePropName="checked"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Checkbox>
                                <FormattedMessage id="user.issues" />
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={24}>
                        <Form.Item
                            name="projectSubmissionNotification"
                            valuePropName="checked"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Checkbox>
                                <FormattedMessage id="user.projectSubmissionNotification" />
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>

                <Row>
                    <Col span={24}>{this.renderAudit(user)}</Col>
                </Row>

                <Form.Item className="buttons">
                    <Space>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            icon={<Icon component={SaveSvg} />}
                            loading={saving}
                        >
                            <FormattedMessage id="button.save" tagName="span" />
                        </Button>
                        <Link to="/admin/users">
                            <Button type="text" size="large">
                                <FormattedMessage id="button.back" tagName="span" />
                            </Button>
                        </Link>
                    </Space>
                </Form.Item>
            </Form>
        );
    };

    render() {
        return (
            <LayoutComponent pageId="users">
                <WrapperComponent title={<FormattedMessage id="user.title" />}>{this.renderContent()}</WrapperComponent>
            </LayoutComponent>
        );
    }
}
export default injectIntl(UserPage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    user?: User;
    saving?: boolean;
}
